import { ArrowLeft, Users, UserCheck, UserX, FileSignature, ExternalLink, Search, Loader2 } from 'lucide-react';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { useCurrentCompany } from '../../hooks/useCurrentCompany';
import { useWaiverComplianceStats, useAllCompanySignedWaivers } from '../../hooks/useWaivers';
import { getWaiverFileUrl } from '../../services/damageWaivers';

export default function WaiverCompliancePage() {
  const navigate = useNavigate();
  const { data: companyId } = useCurrentCompany();
  const { data: compliance, isLoading: loadingStats } = useWaiverComplianceStats(companyId);
  const { data: signedWaivers, isLoading: loadingSigned } = useAllCompanySignedWaivers(companyId);
  const [search, setSearch] = useState('');
  const [viewingId, setViewingId] = useState<string | null>(null);

  const isLoading = loadingStats || loadingSigned;

  const filteredSigned = (signedWaivers || []).filter(w => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      w.signed_by?.toLowerCase().includes(q) ||
      w.template_name?.toLowerCase().includes(q)
    );
  });

  const handleViewPdf = async (signedPdfPath: string, signatureId: string) => {
    setViewingId(signatureId);
    try {
      const url = await getWaiverFileUrl(signedPdfPath);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('Failed to get PDF URL', err);
    } finally {
      setViewingId(null);
    }
  };

  const fmtDate = (iso: string) =>
    new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

  const pct = (signed: number, total: number) =>
    total === 0 ? 0 : Math.round((signed / total) * 100);

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#9B1421] mx-auto" />
          <p className="mt-2 text-[#A9A3A3]">Loading compliance data…</p>
        </div>
      </div>
    );
  }

  const adultPct = pct(compliance?.adults.signed ?? 0, compliance?.adults.total ?? 0);
  const childPct = pct(compliance?.children.signed ?? 0, compliance?.children.total ?? 0);

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Header */}
      <div className="border-b border-white/10 bg-[#111010] pb-3 md:pb-4 shrink-0">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div>
            <h1 className="text-text-primary font-heading text-2xl md:text-4xl font-bold">Waiver Compliance</h1>
            <p className="text-white/80 mt-1">Who has signed and who still needs to</p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/damage-waivers')}
            className="flex items-center gap-2 text-sm text-[#A9A3A3] hover:text-[#EBFFEE] transition-colors shrink-0"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Waivers
          </button>
        </div>
      </div>

      <div className="flex-1 bg-[#111010] overflow-auto scrollbar-styled min-h-0 py-6 w-full space-y-6">

        {/* Stat cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="bg-[#221F1F] border-[#504A4A]">
            <CardContent className="py-5 flex items-center gap-4">
              <div className="p-3 rounded-full bg-[#9B1421]/20">
                <Users className="w-6 h-6 text-[#9B1421]" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#EBFFEE]">{compliance?.adults.total ?? 0}</p>
                <p className="text-[#A9A3A3] text-sm">Total Adult Members</p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#221F1F] border-[#504A4A]">
            <CardContent className="py-5 flex items-center gap-4">
              <div className="p-3 rounded-full bg-green-600/20">
                <UserCheck className="w-6 h-6 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#EBFFEE]">{adultPct}%</p>
                <p className="text-[#A9A3A3] text-sm">
                  Adults Signed ({compliance?.adults.signed ?? 0}/{compliance?.adults.total ?? 0})
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#221F1F] border-[#504A4A]">
            <CardContent className="py-5 flex items-center gap-4">
              <div className="p-3 rounded-full bg-yellow-600/20">
                <UserCheck className="w-6 h-6 text-yellow-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#EBFFEE]">{childPct}%</p>
                <p className="text-[#A9A3A3] text-sm">
                  Minors Signed ({compliance?.children.signed ?? 0}/{compliance?.children.total ?? 0})
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Progress bars */}
        {(compliance?.adults.total ?? 0) > 0 && (
          <Card className="bg-[#221F1F] border-[#504A4A]">
            <CardHeader>
              <CardTitle className="text-[#EBFFEE] text-base">Signature Progress</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {(compliance?.adults.total ?? 0) > 0 && (
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-[#A9A3A3]">Adults</span>
                    <span className="text-[#EBFFEE] font-medium">{adultPct}%</span>
                  </div>
                  <div className="h-3 bg-[#363232] rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${adultPct === 100 ? 'bg-green-500' : adultPct >= 75 ? 'bg-yellow-500' : 'bg-[#9B1421]'}`}
                      style={{ width: `${adultPct}%` }}
                    />
                  </div>
                </div>
              )}
              {(compliance?.children.total ?? 0) > 0 && (
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-[#A9A3A3]">Minors</span>
                    <span className="text-[#EBFFEE] font-medium">{childPct}%</span>
                  </div>
                  <div className="h-3 bg-[#363232] rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${childPct === 100 ? 'bg-green-500' : childPct >= 75 ? 'bg-yellow-500' : 'bg-[#9B1421]'}`}
                      style={{ width: `${childPct}%` }}
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Unsigned adults */}
          {(compliance?.adults.unsigned.length ?? 0) > 0 && (
            <Card className="bg-[#221F1F] border-[#504A4A]">
              <CardHeader>
                <CardTitle className="text-[#EBFFEE] text-base flex items-center gap-2">
                  <UserX className="w-4 h-4 text-[#9B1421]" />
                  Unsigned Adults ({compliance!.adults.unsigned.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-64 overflow-y-auto scrollbar-styled pr-1">
                  {compliance!.adults.unsigned.map(m => (
                    <div key={m.id} className="flex items-center justify-between py-2 border-b border-[#363232] last:border-0">
                      <div>
                        <p className="text-[#EBFFEE] text-sm">
                          {[m.first_name, m.last_name].filter(Boolean).join(' ') || 'Unknown'}
                        </p>
                        {m.email && <p className="text-[#A9A3A3] text-xs">{m.email}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Unsigned children */}
          {(compliance?.children.unsigned.length ?? 0) > 0 && (
            <Card className="bg-[#221F1F] border-[#504A4A]">
              <CardHeader>
                <CardTitle className="text-[#EBFFEE] text-base flex items-center gap-2">
                  <UserX className="w-4 h-4 text-yellow-500" />
                  Unsigned Minors ({compliance!.children.unsigned.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-64 overflow-y-auto scrollbar-styled pr-1">
                  {compliance!.children.unsigned.map(m => (
                    <div key={m.id} className="py-2 border-b border-[#363232] last:border-0">
                      <p className="text-[#EBFFEE] text-sm">
                        {[m.first_name, m.last_name].filter(Boolean).join(' ') || 'Unknown'}
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {(compliance?.adults.unsigned.length ?? 0) === 0 && (compliance?.adults.total ?? 0) > 0 && (
            <Card className="bg-[#221F1F] border-green-600/30">
              <CardContent className="py-8 text-center">
                <UserCheck className="w-10 h-10 text-green-500 mx-auto mb-2" />
                <p className="text-green-400 font-medium">All adults have signed</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Signed waivers table */}
        <Card className="bg-[#221F1F] border-[#504A4A]">
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <CardTitle className="text-[#EBFFEE] text-base flex items-center gap-2">
                <FileSignature className="w-4 h-4 text-[#9B1421]" />
                Signed Waivers ({signedWaivers?.length ?? 0})
              </CardTitle>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A9A3A3]" />
                <input
                  type="text"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search by name or template…"
                  className="pl-9 pr-4 py-1.5 bg-[#363232] border border-[#504A4A] rounded-lg text-sm text-[#EBFFEE] placeholder-[#A9A3A3] focus:outline-none focus:ring-1 focus:ring-[#9B1421] w-full sm:w-56"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {filteredSigned.length === 0 ? (
              <p className="text-[#A9A3A3] text-sm text-center py-8">No signed waivers found.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#504A4A]">
                      <th className="text-left text-[#A9A3A3] font-medium py-2 pr-4">Signer</th>
                      <th className="text-left text-[#A9A3A3] font-medium py-2 pr-4">Template</th>
                      <th className="text-left text-[#A9A3A3] font-medium py-2 pr-4">Type</th>
                      <th className="text-left text-[#A9A3A3] font-medium py-2 pr-4">Date</th>
                      <th className="text-right text-[#A9A3A3] font-medium py-2">PDF</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSigned.map(w => (
                      <tr key={w.id} className="border-b border-[#363232] last:border-0 hover:bg-[#2A2626] transition-colors">
                        <td className="py-2.5 pr-4 text-[#EBFFEE]">{w.signed_by || '—'}</td>
                        <td className="py-2.5 pr-4 text-[#A9A3A3]">{w.template_name || '—'}</td>
                        <td className="py-2.5 pr-4">
                          <span className="text-xs px-2 py-0.5 rounded-full bg-[#363232] text-[#A9A3A3]">
                            {w.child_profile_id ? 'Minor' : 'Adult'}
                          </span>
                        </td>
                        <td className="py-2.5 pr-4 text-[#A9A3A3]">{fmtDate(w.signed_at)}</td>
                        <td className="py-2.5 text-right">
                          <button
                            onClick={() => handleViewPdf(w.signed_pdf_path, w.id)}
                            disabled={viewingId === w.id}
                            className="inline-flex items-center gap-1 text-[#9B1421] hover:text-[#C41B2E] transition-colors disabled:opacity-50"
                          >
                            {viewingId === w.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <ExternalLink className="w-4 h-4" />
                            )}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
